import { useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Download } from 'lucide-react';

interface Props {
  images: { src: string; name: string }[];
  index: number;
  onClose: () => void;
  onGoTo: (i: number) => void;
}

export default function ImageCarouselModal({ images, index, onClose, onGoTo }: Props) {
  const current = images[index];
  const total   = images.length;

  const prev = useCallback(() => onGoTo((index - 1 + total) % total), [index, total, onGoTo]);
  const next = useCallback(() => onGoTo((index + 1) % total),         [index, total, onGoTo]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape')     onClose();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft')  prev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, prev, next]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Üst araç çubuğu */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between px-4 py-3 pointer-events-none">
        {/* Sayaç (ortada) */}
        <div className="flex-1 flex justify-center">
          <span className="pointer-events-auto rounded-full bg-black/40 px-3 py-1 text-sm text-white">
            {index + 1} / {total}
          </span>
        </div>
        {/* Sağ butonlar */}
        <div className="flex items-center gap-2 pointer-events-auto">
          <a
            href={current.src}
            download={current.name}
            target="_blank"
            rel="noreferrer"
            onClick={e => e.stopPropagation()}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/25 transition-colors"
            title="İndir"
          >
            <Download size={16} />
          </a>
          <button
            onClick={e => { e.stopPropagation(); onClose(); }}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/25 transition-colors"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Sol ok */}
      {total > 1 && (
        <button
          onClick={e => { e.stopPropagation(); prev(); }}
          className="absolute left-3 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/25 transition-colors z-10"
        >
          <ChevronLeft size={26} />
        </button>
      )}

      {/* Ana resim */}
      <div
        className="flex flex-col items-center gap-3 px-16"
        style={{ maxHeight: total > 1 ? 'calc(85vh - 80px)' : '85vh', maxWidth: '85vw' }}
      >
        <img
          key={current.src}
          src={current.src}
          alt={current.name}
          className="rounded-xl object-contain shadow-2xl"
          style={{ maxHeight: total > 1 ? 'calc(78vh - 80px)' : '78vh', maxWidth: '100%' }}
          draggable={false}
        />
        <p className="text-sm text-white/60 truncate max-w-sm">{current.name}</p>
      </div>

      {/* Sağ ok */}
      {total > 1 && (
        <button
          onClick={e => { e.stopPropagation(); next(); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/25 transition-colors z-10"
        >
          <ChevronRight size={26} />
        </button>
      )}

      {/* Thumbnail şeridi */}
      {total > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[80vw] px-2 py-1">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={e => { e.stopPropagation(); onGoTo(i); }}
              className={`flex-shrink-0 h-14 w-14 rounded-lg overflow-hidden border-2 transition-all ${
                i === index
                  ? 'border-white scale-110 shadow-lg'
                  : 'border-transparent opacity-50 hover:opacity-80 hover:border-white/50'
              }`}
            >
              <img src={img.src} alt={img.name} className="h-full w-full object-cover" draggable={false} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
